import { InputWithLabel } from '@adotel/components'
import { useMutation, useQuery } from '@apollo/client'
import {
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography
} from '@material-ui/core'
import SaveIcon from '@material-ui/icons/Save'
import { useState } from 'react'
import { toast } from 'react-toastify'

import { getCredentials } from '../../cache/auth'
import {
  EXPORT_POLICY_CONFIGURATION_QUERY,
  UPDATE_EXPORT_POLICY_CONFIGURATION_MUTATION
} from '../../graphql/adminConfig'
import { getTranslate } from '../../localization'
import { ROLE } from '../../staticRoleManagement/types'
import UpdatedByName from './UpdatedByName'
import useStyles from './index.styles'

export type ExportOperation =
  | 'TENANT_DATA_EXPORT'
  | 'BOOKING_LIST_EXCEL'
  | 'BOOKING_ROOM_STAYS_EXCEL'
  | 'PAYMENT_REPORT_EXCEL'
  | 'BOOKING_ROOM_STAY_CSV'
  | 'BOOKING_SETTLEMENT_CSV'

export type ExportLimits = {
  maxRows: number
  maxFileSizeMegabytes: number
}

export type ExportOperationOverride = {
  operation: ExportOperation
  limits: ExportLimits
}

export type ExportPolicyConfiguration = {
  defaultLimits: ExportLimits
  operationOverrides: ExportOperationOverride[]
  revision: number
  updatedAt: string
  updatedBy: string
}

const operations: {
  value: ExportOperation
  label: string
  maxRows: number
  maxFileSizeMegabytes: number
}[] = [
  {
    value: 'TENANT_DATA_EXPORT',
    label: 'Tenant data export',
    maxRows: 10000000,
    maxFileSizeMegabytes: 2048
  },
  {
    value: 'BOOKING_LIST_EXCEL',
    label: 'Booking list Excel',
    maxRows: 100000,
    maxFileSizeMegabytes: 64
  },
  {
    value: 'BOOKING_ROOM_STAYS_EXCEL',
    label: 'Booking room stays Excel',
    maxRows: 100000,
    maxFileSizeMegabytes: 64
  },
  {
    value: 'PAYMENT_REPORT_EXCEL',
    label: 'Payment report Excel',
    maxRows: 100000,
    maxFileSizeMegabytes: 64
  },
  {
    value: 'BOOKING_ROOM_STAY_CSV',
    label: 'Booking room stay CSV',
    maxRows: 100000,
    maxFileSizeMegabytes: 64
  },
  {
    value: 'BOOKING_SETTLEMENT_CSV',
    label: 'Booking settlement CSV',
    maxRows: 100000,
    maxFileSizeMegabytes: 64
  }
]

const emptyConfiguration: ExportPolicyConfiguration = {
  defaultLimits: { maxRows: 0, maxFileSizeMegabytes: 0 },
  operationOverrides: [],
  revision: 0,
  updatedAt: '',
  updatedBy: ''
}

const limitsAreInvalid = (
  limits: ExportLimits,
  maxFileSizeMegabytes = 2048,
  maxRows = 10000000
) =>
  limits.maxRows < 1 ||
  limits.maxRows > maxRows ||
  limits.maxFileSizeMegabytes < 1 ||
  limits.maxFileSizeMegabytes > maxFileSizeMegabytes

const errorCode = (error: any) =>
  error?.graphQLErrors?.[0]?.extensions?.code as string | undefined

export default function AdminConfigTenantExportTab() {
  const classes = useStyles()
  const isAdmin = getCredentials()?.role === ROLE.ADMIN
  const [configuration, setConfiguration] =
    useState<ExportPolicyConfiguration>(emptyConfiguration)

  const { loading, refetch } = useQuery<{
    exportPolicyConfiguration: ExportPolicyConfiguration
  }>(EXPORT_POLICY_CONFIGURATION_QUERY, {
    skip: !isAdmin,
    fetchPolicy: 'network-only',
    onCompleted: (data) => setConfiguration(data.exportPolicyConfiguration),
    onError: () =>
      toast.error(getTranslate('Unable to load the export policy.'))
  })

  const [updateConfiguration, { loading: saving }] = useMutation(
    UPDATE_EXPORT_POLICY_CONFIGURATION_MUTATION,
    {
      onCompleted: (data) => {
        setConfiguration(data.updateExportPolicyConfiguration)
        toast.success(getTranslate('Export policy saved'))
      },
      onError: (error) => {
        if (errorCode(error) === 'EXPORT_POLICY_CONFLICT') {
          toast.error(
            getTranslate(
              'The export policy changed. Latest values have been reloaded.'
            )
          )
          void refetch()
          return
        }
        toast.error(getTranslate('Unable to save the export policy.'))
      }
    }
  )

  const updateDefaultLimit =
    (field: keyof ExportLimits) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value
      if (!/^\d*$/.test(value)) return
      setConfiguration((current) => ({
        ...current,
        defaultLimits: {
          ...current.defaultLimits,
          [field]: Number(value)
        }
      }))
    }

  const updateOverrideLimit =
    (operation: ExportOperation, field: keyof ExportLimits) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value
      if (!/^\d*$/.test(value)) return
      setConfiguration((current) => ({
        ...current,
        operationOverrides: current.operationOverrides.map((override) =>
          override.operation === operation
            ? {
                ...override,
                limits: { ...override.limits, [field]: Number(value) }
              }
            : override
        )
      }))
    }

  const toggleOverride = (
    operation: ExportOperation,
    maxFileSizeMegabytes: number,
    maxRows: number
  ) => {
    setConfiguration((current) => {
      const exists = current.operationOverrides.some(
        (override) => override.operation === operation
      )
      return {
        ...current,
        operationOverrides: exists
          ? current.operationOverrides.filter(
              (override) => override.operation !== operation
            )
          : [
              ...current.operationOverrides,
              {
                operation,
                limits: {
                  ...current.defaultLimits,
                  maxRows: Math.min(current.defaultLimits.maxRows, maxRows),
                  maxFileSizeMegabytes: Math.min(
                    current.defaultLimits.maxFileSizeMegabytes,
                    maxFileSizeMegabytes
                  )
                }
              }
            ]
      }
    })
  }

  const invalid =
    limitsAreInvalid(configuration.defaultLimits) ||
    operations.some((operation) => {
      const override = configuration.operationOverrides.find(
        (item) => item.operation === operation.value
      )
      return limitsAreInvalid(
        override?.limits ?? configuration.defaultLimits,
        operation.maxFileSizeMegabytes,
        operation.maxRows
      )
    })

  const save = () =>
    updateConfiguration({
      variables: {
        input: {
          defaultLimits: {
            maxRows: configuration.defaultLimits.maxRows,
            maxFileSizeMegabytes:
              configuration.defaultLimits.maxFileSizeMegabytes
          },
          operationOverrides: configuration.operationOverrides.map(
            (override) => ({
              operation: override.operation,
              limits: {
                maxRows: override.limits.maxRows,
                maxFileSizeMegabytes: override.limits.maxFileSizeMegabytes
              }
            })
          ),
          expectedRevision: configuration.revision
        }
      }
    })

  if (!isAdmin) {
    return (
      <Typography role="alert">
        {getTranslate('You are not allowed to manage export policy.')}
      </Typography>
    )
  }

  return (
    <div className={classes.root}>
      <section className={classes.section}>
        <Typography variant="h4" className={classes.title}>
          {getTranslate('Export Policy')}
        </Typography>
        <Typography variant="h5" className={classes.subtitle}>
          {getTranslate(
            'Set defaults for every export. Enable an operation override only when it needs stricter or larger limits.'
          )}
        </Typography>
        <Typography variant="h5" className={classes.sectionTitle}>
          {getTranslate('Global defaults')}
        </Typography>
        <div className={classes.inputs}>
          <InputWithLabel
            id="export-policy-default-max-rows"
            name="export-policy-default-max-rows"
            label={getTranslate('Maximum Rows Per Export')}
            helpertext={getTranslate('Allowed range: 1 to 10,000,000 rows.')}
            value={configuration.defaultLimits.maxRows}
            onChange={updateDefaultLimit('maxRows')}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            inputSize="small"
            className={classes.input}
            error={
              configuration.defaultLimits.maxRows < 1 ||
              configuration.defaultLimits.maxRows > 10000000
            }
            aria-invalid={
              configuration.defaultLimits.maxRows < 1 ||
              configuration.defaultLimits.maxRows > 10000000
            }
          />
          <InputWithLabel
            id="export-policy-default-max-file-size"
            name="export-policy-default-max-file-size"
            label={getTranslate('Maximum File Size (MB)')}
            helpertext={getTranslate('Allowed range: 1 to 2,048 MB.')}
            value={configuration.defaultLimits.maxFileSizeMegabytes}
            onChange={updateDefaultLimit('maxFileSizeMegabytes')}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            inputSize="small"
            className={classes.input}
            error={
              configuration.defaultLimits.maxFileSizeMegabytes < 1 ||
              configuration.defaultLimits.maxFileSizeMegabytes > 2048
            }
            aria-invalid={
              configuration.defaultLimits.maxFileSizeMegabytes < 1 ||
              configuration.defaultLimits.maxFileSizeMegabytes > 2048
            }
          />
        </div>
      </section>

      <section className={classes.section}>
        <Typography variant="h5" className={classes.sectionTitle}>
          {getTranslate('Operation overrides')}
        </Typography>
        <Typography variant="h5" className={classes.overrideHelp}>
          {getTranslate(
            'Operations without an override automatically use the global defaults.'
          )}
        </Typography>
        <div className={classes.operations}>
          {operations.map((operation) => {
            const override = configuration.operationOverrides.find(
              (item) => item.operation === operation.value
            )
            return (
              <div className={classes.operation} key={operation.value}>
                <div className={classes.operationHeader}>
                  <Typography variant="h5" className={classes.operationName}>
                    {getTranslate(operation.label)}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={Boolean(override)}
                        onChange={() =>
                          toggleOverride(
                            operation.value,
                            operation.maxFileSizeMegabytes,
                            operation.maxRows
                          )
                        }
                        inputProps={{
                          'aria-label': `${getTranslate(
                            operation.label
                          )}: ${getTranslate('Override global limits')}`
                        }}
                      />
                    }
                    label={getTranslate('Override global limits')}
                  />
                </div>
                {!override &&
                  configuration.defaultLimits.maxFileSizeMegabytes >
                    operation.maxFileSizeMegabytes && (
                    <Typography variant="body2" color="error" role="alert">
                      {getTranslate(
                        `Global default exceeds this operation's ${operation.maxFileSizeMegabytes} MB safety ceiling. Enable custom limits.`
                      )}
                    </Typography>
                  )}
                {override && (
                  <div className={classes.inputs}>
                    <InputWithLabel
                      id={`export-policy-${operation.value}-max-rows`}
                      name={`export-policy-${operation.value}-max-rows`}
                      label={getTranslate('Maximum Rows')}
                      helpertext={getTranslate(
                        `Allowed range: 1 to ${operation.maxRows.toLocaleString()} rows.`
                      )}
                      value={override.limits.maxRows}
                      onChange={updateOverrideLimit(operation.value, 'maxRows')}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      inputSize="small"
                      className={classes.input}
                      error={
                        override.limits.maxRows < 1 ||
                        override.limits.maxRows > operation.maxRows
                      }
                      aria-invalid={
                        override.limits.maxRows < 1 ||
                        override.limits.maxRows > operation.maxRows
                      }
                    />
                    <InputWithLabel
                      id={`export-policy-${operation.value}-max-file-size`}
                      name={`export-policy-${operation.value}-max-file-size`}
                      label={getTranslate('Maximum File Size (MB)')}
                      helpertext={getTranslate(
                        `Allowed range: 1 to ${operation.maxFileSizeMegabytes.toLocaleString()} MB.`
                      )}
                      value={override.limits.maxFileSizeMegabytes}
                      onChange={updateOverrideLimit(
                        operation.value,
                        'maxFileSizeMegabytes'
                      )}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      inputSize="small"
                      className={classes.input}
                      error={
                        override.limits.maxFileSizeMegabytes < 1 ||
                        override.limits.maxFileSizeMegabytes >
                          operation.maxFileSizeMegabytes
                      }
                      aria-invalid={
                        override.limits.maxFileSizeMegabytes < 1 ||
                        override.limits.maxFileSizeMegabytes >
                          operation.maxFileSizeMegabytes
                      }
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </section>

      {configuration.revision > 0 && (
        <Typography variant="body2" className={classes.metadata}>
          {getTranslate('Revision')}: {configuration.revision}
          {configuration.updatedBy && (
            <>
              {' · '}
              {getTranslate('Last updated by')}:{' '}
              <UpdatedByName
                key={configuration.updatedBy}
                userID={configuration.updatedBy}
              />
            </>
          )}
        </Typography>
      )}

      <div className={classes.actions}>
        <Button
          variant="outlined"
          disabled={loading || saving}
          onClick={() => refetch()}>
          <Typography variant="h4" className={classes.buttonText}>
            {getTranslate('Reset')}
          </Typography>
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          disabled={loading || saving || invalid}
          className={classes.saveButton}
          onClick={save}>
          <Typography variant="h4" className={classes.whiteText}>
            {getTranslate('Save Export Policy')}
          </Typography>
          {saving && (
            <CircularProgress size={16} className={classes.progress} />
          )}
        </Button>
      </div>
    </div>
  )
}
